<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {useQuasar} from 'quasar';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRouter} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {useUserStore} from '@/ts/store/user-store';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useLanguageChange} from '@/ts/composables/stateful/use-language-change';
import {DISABLE_LOADING} from '@/ts/utils/consts';
import {isLegalConsentPending} from '@/ts/composables/pure/use-legal-consent-gate';
import TermsChangeModal from '@/vue/molecules/terms-change-modal.vue';
import OopsTemplate from '@/vue/templates/oops-template.vue';
import type {UserConsents} from '@/ts/types/dto/user-dto';
import type {GetLegalDocsResponse} from '@/ts/types/dto/tos-dto';

const api = useApi();
const router = useRouter();
const userStore = useUserStore();
const logger = useLogger();
const {syncBackendLanguage} = useLanguageChange();
const $q = useQuasar();

const showModal = ref(false);
const userConsents = ref<UserConsents | null>(null);
const legalDocs = ref<GetLegalDocsResponse | null>(null);
const loadError = ref<string | null>(null);

function hideLoader(): void {
  if (!DISABLE_LOADING) {
    $q.loading.hide();
  }
}

/** Leaves for home; the navigation guard turns that into wherever the consent gate interrupted the user. */
function leave(): void {
  void router.replace(pagesPath.main.home);
}

onMounted(async() => {
  if (!DISABLE_LOADING) {
    $q.loading.show();
  }
  try {
    const userData = await api.me.getMe();
    // Sync the user's preferred language BEFORE fetching legal docs:
    // /legal-docs picks the response language from the Accept-Language
    // header. Fetching them in parallel with /me returns docs in the
    // pre-login locale and the modal renders in the wrong language
    // (IPD-2614). If languages differ this triggers a full reload —
    // skip the rest and leave the spinner up so the user doesn't see the
    // empty page during the brief window before the browser unloads.
    if (syncBackendLanguage(userData.language)) {
      return;
    }
    if (!isLegalConsentPending(userData)) {
      // Reached with nothing to consent to (a reload, a bookmark, the back button): asking again would
      // offer a decline that logs the user out over nothing.
      userStore.setUserData(userData);
      hideLoader();
      leave();
      return;
    }
    const legalDocsData = await api.public.getLegalDocs();

    userConsents.value = userData.consents ?? null;
    legalDocs.value = legalDocsData;
    showModal.value = true;
    hideLoader();
  } catch (error) {
    logger.error('Failed to load consent data:', error)();
    // Show error page instead of redirecting - this is an irrecoverable state
    loadError.value = 'Failed to load consent data';
    hideLoader();
  }
});

/**
 * The navigation guard reads consents from the user store, so it is refreshed before leaving:
 * stale data would still show the consent as pending and send the user straight back here. If the
 * backend still reports it pending (a document version moved meanwhile), the modal is shown again
 * with the documents reloaded, rather than leaving the user on an empty page.
 */
async function continueWithConsentGiven(): Promise<void> {
  if (!DISABLE_LOADING) {
    $q.loading.show();
  }
  try {
    const userData = await api.me.getMe();
    userStore.setUserData(userData);
    if (isLegalConsentPending(userData)) {
      legalDocs.value = await api.public.getLegalDocs();
      userConsents.value = userData.consents ?? null;
      showModal.value = true;
      hideLoader();
      return;
    }
  } catch (error) {
    logger.error('Failed to refresh the user after consenting:', error)();
    // Unknown gates nothing, so the guard lets the user through and the app shell loads them afresh.
    userStore.setUserData(null);
  }
  hideLoader();
  leave();
}

function handleConsentsAccepted(): void {
  showModal.value = false;
  void continueWithConsentGiven();
}

function handleConsentsDeclined(): void {
  showModal.value = false;
  // Same per-user-state cleanup as endLocalSession's non-idToken branch:
  // userStore.logout() resets reactive Pinia state, localStorage.clear()
  // wipes per-user keys (tour-seen, preferences, etc.) so the next user on
  // this browser starts fresh, persistentStorage.clear() re-establishes
  // browser-default lang.
  userStore.logout();
  localStorage.clear();
  persistentStorage.clear();
  void router.replace(pagesPath.public.public);
}
</script>

<template>
  <div class="consent-required-page">
    <oops-template
      v-if="loadError"
      text-style="dark"
      :text="$t('the_server_encountered')"
      error-type="crash"
    />
    <terms-change-modal
      v-else-if="showModal && userConsents && legalDocs"
      v-model="showModal"
      :consents="userConsents"
      :legal-docs="legalDocs"
      @accepted="handleConsentsAccepted"
      @declined="handleConsentsDeclined"
    />
  </div>
</template>

<style scoped lang="scss">
.consent-required-page {
  min-height: 100vh;
  background: var(--Background-background-primary);
}
</style>